import NightlightIcon from "@mui/icons-material/Nightlight";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import type { ReactNode } from "react";
import { SHIFT_WINDOW } from "../../../../theme/tokens";
import { ShiftType } from "../../../../utils/models";
import { MONTHLY_OTHER_WINDOW_KEY, type MonthlyShiftWindow } from "./monthlyJobUtils";

/**
 * How one shift-time window is painted — the SINGLE derivation shared by the grid cell, the drill-down
 * sidebar, and the legend, so the three can never disagree about which window is which.
 *
 * The band (Day / Night, from `getShiftTypeFromStartTime`) picks the hue family; the window's position
 * within its band picks the ramp step (`SHIFT_WINDOW.DAY_TINTS` / `NIGHT_TINTS`, earliest = lightest).
 * The ramp cycles if a department ever exceeds it — the label and the glyph still tell the rows apart,
 * so color is never the only carrier of meaning (the house rule `CoverageDeltaCell` states explicitly).
 */
export type MonthlyWindowPresentation = {
	/** Row background. Safe under 11–13px text: see the contrast note on `SHIFT_WINDOW`. */
	tint: string;
	/** 3px left rail — the peripheral scan signal that groups a column of the same window. Drawn by the
	    day drawer's list rows and the filter chips; the grid tile's rows are pills and carry the tint alone. */
	rail: string;
	/** Fixed ink that clears AA on every step of this band. */
	ink: string;
	/** Day/Night glyph, or `null` for the band-less `Other` bucket. */
	glyph: ReactNode;
};

const glyphFor = (band: ShiftType): ReactNode =>
	band === ShiftType.Night ? (
		<NightlightIcon aria-hidden sx={{ fontSize: 11, color: SHIFT_WINDOW.NIGHT_INK }} />
	) : (
		<WbSunnyIcon aria-hidden sx={{ fontSize: 11, color: SHIFT_WINDOW.DAY_INK }} />
	);

export const getWindowPresentation = (window: MonthlyShiftWindow): MonthlyWindowPresentation => {
	if (window.key === MONTHLY_OTHER_WINDOW_KEY || window.band === null) {
		return { tint: SHIFT_WINDOW.OTHER_TINT, rail: SHIFT_WINDOW.OTHER_RAIL, ink: SHIFT_WINDOW.OTHER_INK, glyph: null };
	}
	const isNight = window.band === ShiftType.Night;
	const tints = isNight ? SHIFT_WINDOW.NIGHT_TINTS : SHIFT_WINDOW.DAY_TINTS;
	return {
		tint: tints[window.bandIndex % tints.length],
		rail: isNight ? SHIFT_WINDOW.NIGHT_RAIL : SHIFT_WINDOW.DAY_RAIL,
		ink: isNight ? SHIFT_WINDOW.NIGHT_INK : SHIFT_WINDOW.DAY_INK,
		glyph: glyphFor(window.band),
	};
};
